/**
 * HTTPS Validation Utility
 * Ensures all API communications use HTTPS in production
 */

export class HTTPSValidator {
  constructor() {
    this.isProduction = import.meta.env.PROD
    this.isDevelopment = import.meta.env.DEV
  }

  /**
   * Validate and enforce HTTPS for URLs
   */
  validateUrl(url) {
    if (!url) return url

    try {
      const urlObj = new URL(url)
      
      // In production, enforce HTTPS
      if (this.isProduction && urlObj.protocol === 'http:') {
        console.warn(`Converting HTTP to HTTPS for production: ${url}`)
        urlObj.protocol = 'https:'
        return urlObj.toString()
      }
      
      return url
    } catch (error) {
      console.error('Invalid URL provided:', url)
      return url
    }
  }

  /**
   * Check if current page is served over HTTPS
   */
  isSecureContext() {
    return window.isSecureContext || window.location.protocol === 'https:'
  }

  /**
   * Warn if not using HTTPS in production
   */
  validateCurrentContext() {
    if (this.isProduction && !this.isSecureContext()) {
      console.error('⚠️ SECURITY WARNING: Application is not served over HTTPS in production!')
      
      // Show user warning
      if (typeof window !== 'undefined') {
        const warning = document.createElement('div')
        warning.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #ff4444;
          color: white;
          padding: 10px;
          text-align: center;
          z-index: 10000;
          font-weight: bold;
        `
        warning.textContent = '⚠️ This site is not secure. Please use HTTPS.'
        document.body.appendChild(warning)
      }
    }
  }

  /**
   * Validate API endpoint URL
   */
  validateApiEndpoint(endpoint) {
    const baseUrl = import.meta.env.VITE_API_URL || window.location.origin
    const fullUrl = new URL(endpoint, baseUrl).toString()
    return this.validateUrl(fullUrl)
  }

  /**
   * Check if external URL is secure
   */
  isSecureExternalUrl(url) {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  /**
   * Get secure WebSocket URL
   */
  getSecureWebSocketUrl(url) {
    if (!url) return url

    try {
      const urlObj = new URL(url)
      
      // Convert ws:// to wss:// in production
      if (this.isProduction && urlObj.protocol === 'ws:') {
        urlObj.protocol = 'wss:'
        return urlObj.toString()
      }
      
      return url
    } catch (error) {
      console.error('Invalid WebSocket URL:', url)
      return url
    }
  }

  /**
   * Initialize HTTPS validation
   */
  init() {
    // Validate current context
    this.validateCurrentContext()

    // Set up Content Security Policy if not already set
    if (this.isProduction && !document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
      const cspMeta = document.createElement('meta')
      cspMeta.setAttribute('http-equiv', 'Content-Security-Policy')
      cspMeta.setAttribute('content', 
        "default-src 'self'; " +
        "script-src 'self' 'unsafe-inline' 'unsafe-eval'; " +
        "style-src 'self' 'unsafe-inline'; " +
        "img-src 'self' data: https:; " +
        "font-src 'self' data:; " +
        "connect-src 'self' https:; " +
        "frame-ancestors 'none'; " +
        "upgrade-insecure-requests"
      )
      document.head.appendChild(cspMeta)
    }

    // Monitor for mixed content
    if (this.isProduction) {
      this.monitorMixedContent()
    }
  }

  /**
   * Monitor for mixed content issues
   */
  monitorMixedContent() {
    // Listen for security policy violations
    document.addEventListener('securitypolicyviolation', (event) => {
      console.error('Content Security Policy violation:', {
        violatedDirective: event.violatedDirective,
        blockedURI: event.blockedURI,
        originalPolicy: event.originalPolicy
      })
      
      // Report to monitoring service if available
      if (window.reportError) {
        window.reportError('CSP Violation', {
          directive: event.violatedDirective,
          uri: event.blockedURI
        })
      }
    })

    // Check for mixed content in images, scripts, etc.
    const checkMixedContent = () => {
      if (!this.isSecureContext()) return

      // Check images
      document.querySelectorAll('img[src^="http:"]').forEach(img => {
        console.warn('Mixed content detected - HTTP image on HTTPS page:', img.src)
      })

      // Check scripts
      document.querySelectorAll('script[src^="http:"]').forEach(script => {
        console.warn('Mixed content detected - HTTP script on HTTPS page:', script.src)
      })

      // Check stylesheets
      document.querySelectorAll('link[href^="http:"]').forEach(link => {
        console.warn('Mixed content detected - HTTP stylesheet on HTTPS page:', link.href)
      })
    }

    // Check on DOM ready and periodically
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkMixedContent)
    } else {
      checkMixedContent()
    }

    // Check periodically for dynamically added content
    setInterval(checkMixedContent, 30000) // Every 30 seconds
  }
}

// Create global instance
export const httpsValidator = new HTTPSValidator()

// Auto-initialize
if (typeof window !== 'undefined') {
  httpsValidator.init()
}

// Export utility functions
export const validateUrl = (url) => httpsValidator.validateUrl(url)
export const validateApiEndpoint = (endpoint) => httpsValidator.validateApiEndpoint(endpoint)
export const isSecureContext = () => httpsValidator.isSecureContext()
export const getSecureWebSocketUrl = (url) => httpsValidator.getSecureWebSocketUrl(url)

export default httpsValidator